import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Shield, Lock, LogOut, ArrowLeft, User, Mail, Phone, RefreshCw } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PW_KEY = 'salusflow_admin_pw';

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('en-GB', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

const Gate = ({ onAuth }) => {
  const [pw, setPw] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await axios.post(`${API}/admin/verify`, {}, { headers: { 'X-Admin-Password': pw } });
      sessionStorage.setItem(PW_KEY, pw);
      onAuth(pw);
    } catch (err) {
      setError('Incorrect password.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-gate-wrap">
      <form className="admin-gate-card" onSubmit={submit} data-testid="admin-gate-form">
        <div className="admin-gate-icon"><Lock className="w-6 h-6" /></div>
        <h1 className="admin-gate-title">Admin Access</h1>
        <p className="admin-gate-sub">Enter the admin password to view chat conversations.</p>
        <input
          type="password"
          value={pw}
          onChange={(e) => setPw(e.target.value)}
          placeholder="Admin password"
          className="admin-gate-input"
          autoFocus
          required
          data-testid="admin-password-input"
        />
        {error && <div className="admin-gate-error" data-testid="admin-gate-error">{error}</div>}
        <button
          type="submit"
          className="admin-gate-btn"
          disabled={loading || !pw}
          data-testid="admin-gate-submit"
        >
          {loading ? 'Verifying...' : 'Unlock'}
        </button>
      </form>
    </div>
  );
};

const ConversationDetail = ({ password, sessionId, onBack }) => {
  const [convo, setConvo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const { data } = await axios.get(`${API}/admin/conversations/${sessionId}`, {
          headers: { 'X-Admin-Password': password },
        });
        if (!cancelled) setConvo(data);
      } catch (err) {
        if (!cancelled) setError('Failed to load conversation.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [password, sessionId]);

  return (
    <div className="admin-detail" data-testid="admin-conversation-detail">
      <button type="button" onClick={onBack} className="admin-back-btn" data-testid="admin-back-btn">
        <ArrowLeft className="w-4 h-4" /> Back to all conversations
      </button>

      {loading && <div className="admin-empty">Loading transcript...</div>}
      {error && <div className="admin-error">{error}</div>}

      {convo && (
        <>
          <div className="admin-detail-meta">
            <div>
              <div className="admin-detail-label">Session</div>
              <div className="admin-detail-value" data-testid="detail-session-id">{convo.session_id}</div>
            </div>
            <div>
              <div className="admin-detail-label">Started</div>
              <div className="admin-detail-value">{formatDate(convo.created_at)}</div>
            </div>
            <div>
              <div className="admin-detail-label">Last activity</div>
              <div className="admin-detail-value">{formatDate(convo.updated_at)}</div>
            </div>
            <div>
              <div className="admin-detail-label">Model</div>
              <div className="admin-detail-value">{convo.model || '—'}</div>
            </div>
          </div>

          {(convo.contact && (convo.contact.name || convo.contact.email || convo.contact.phone)) && (
            <div className="admin-contact-card" data-testid="detail-contact-card">
              <div className="admin-contact-title">Captured contact</div>
              <div className="admin-contact-list">
                {convo.contact.name && (
                  <div className="admin-contact-item"><User className="w-4 h-4" /> {convo.contact.name}</div>
                )}
                {convo.contact.email && (
                  <div className="admin-contact-item"><Mail className="w-4 h-4" /> {convo.contact.email}</div>
                )}
                {convo.contact.phone && (
                  <div className="admin-contact-item"><Phone className="w-4 h-4" /> {convo.contact.phone}</div>
                )}
              </div>
            </div>
          )}

          <div className="admin-transcript" data-testid="detail-transcript">
            {(convo.messages || []).map((m, i) => (
              <div key={i} className={`admin-msg admin-msg-${m.role}`}>
                <div className="admin-msg-role">{m.role === 'user' ? 'Visitor' : 'Assistant'}</div>
                <div className="admin-msg-content">{m.content}</div>
                {m.timestamp && <div className="admin-msg-time">{formatDate(m.timestamp)}</div>}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const ConversationsList = ({ password, onOpen, onLogout }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchList = async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.get(`${API}/admin/conversations`, {
        headers: { 'X-Admin-Password': password },
      });
      setItems(data.conversations || []);
    } catch (err) {
      setError('Failed to load conversations.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList();
  }, [password]);

  return (
    <div data-testid="admin-conversations-list">
      <div className="admin-toolbar">
        <div className="admin-toolbar-title">
          <Shield className="w-5 h-5" />
          <span>Chat Conversations</span>
          <span className="admin-count-pill" data-testid="admin-count">{items.length}</span>
        </div>
        <div className="admin-toolbar-actions">
          <button type="button" onClick={fetchList} className="admin-icon-btn" data-testid="admin-refresh-btn" aria-label="Refresh">
            <RefreshCw className="w-4 h-4" />
          </button>
          <button type="button" onClick={onLogout} className="admin-icon-btn" data-testid="admin-logout-btn" aria-label="Sign out">
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </div>

      {loading && <div className="admin-empty">Loading...</div>}
      {error && <div className="admin-error">{error}</div>}
      {!loading && !error && items.length === 0 && (
        <div className="admin-empty">No conversations yet. Send a chat message from the site to test.</div>
      )}

      <div className="admin-list">
        {items.map((c) => (
          <button
            key={c.session_id}
            type="button"
            className="admin-list-item"
            onClick={() => onOpen(c.session_id)}
            data-testid={`admin-list-item-${c.session_id}`}
          >
            <div className="admin-list-main">
              <div className="admin-list-snippet">{c.snippet || '(empty)'}</div>
              <div className="admin-list-meta">
                <span>{formatDate(c.updated_at)}</span>
                <span>•</span>
                <span>{c.message_count} messages</span>
                {c.has_contact && (
                  <>
                    <span>•</span>
                    <span className="admin-contact-pill">Contact captured</span>
                  </>
                )}
              </div>
            </div>
            <div className="admin-list-arrow">›</div>
          </button>
        ))}
      </div>
    </div>
  );
};

const AdminConversations = () => {
  const [password, setPassword] = useState(() => sessionStorage.getItem(PW_KEY) || '');
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    // Re-verify saved password once on mount to avoid stale creds
    if (password) {
      axios
        .post(`${API}/admin/verify`, {}, { headers: { 'X-Admin-Password': password } })
        .catch(() => {
          sessionStorage.removeItem(PW_KEY);
          setPassword('');
        });
    }
  }, [password]);

  if (!password) return <Gate onAuth={setPassword} />;

  return (
    <div className="admin-shell" data-testid="admin-shell">
      <div className="admin-container">
        {openId ? (
          <ConversationDetail
            password={password}
            sessionId={openId}
            onBack={() => setOpenId(null)}
          />
        ) : (
          <ConversationsList
            password={password}
            onOpen={setOpenId}
            onLogout={() => {
              sessionStorage.removeItem(PW_KEY);
              setPassword('');
            }}
          />
        )}
      </div>
    </div>
  );
};

export default AdminConversations;
